import type { RankTier } from '@shared/types';

interface RankMedalProps {
  rank?: RankTier;
  size?: number;
}

const PALETTE: Record<RankTier, { a: string; b: string; glow: string; ring?: string }> = {
  bronze:     { a: '#cd7f32', b: '#8b4513', glow: 'rgba(205,127,50,0.5)' },
  silver:     { a: '#e8e8e8', b: '#999',    glow: 'rgba(220,220,220,0.5)' },
  gold:       { a: '#F4C724', b: '#a07c00', glow: 'rgba(244,199,36,0.6)' },
  platinum:   { a: '#d4f5fb', b: '#22D3EE', glow: 'rgba(34,211,238,0.6)' },
  diamond:    { a: '#a4d8ff', b: '#3B82F6', glow: 'rgba(59,130,246,0.6)' },
  master:     { a: '#d8b4fe', b: '#9B5DE5', glow: 'rgba(155,93,229,0.75)', ring: '#22D3EE' },
  challenger: { a: '#fff8c4', b: '#F4C724', glow: 'rgba(244,199,36,0.9)',  ring: '#E63946' },
};

export function RankMedal({ rank = 'gold', size = 64 }: RankMedalProps) {
  const p = PALETTE[rank];
  const gradId = `medal-${rank}-${size}`;
  const isMaster = rank === 'master';
  const isChallenger = rank === 'challenger';
  const isElite = isMaster || isChallenger;

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        filter: `drop-shadow(0 0 ${size / (isElite ? 4 : 6)}px ${p.glow})`,
      }}
    >
      <svg viewBox="0 0 100 100" width={size} height={size}>
        <defs>
          <radialGradient id={gradId} cx="50%" cy="40%">
            <stop offset="0%" stopColor={p.a} />
            <stop offset="100%" stopColor={p.b} />
          </radialGradient>
          {isChallenger && (
            <linearGradient id={`${gradId}-rainbow`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#E63946" />
              <stop offset="33%" stopColor="#F4C724" />
              <stop offset="66%" stopColor="#22D3EE" />
              <stop offset="100%" stopColor="#9B5DE5" />
            </linearGradient>
          )}
        </defs>

        {/* Outer dashed ring — for elite tiers use a solid double ring + accent color */}
        {isElite ? (
          <>
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke={p.ring}
              strokeWidth="1.5"
              opacity="0.85"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke={p.b}
              strokeWidth="2"
              opacity="0.95"
            />
          </>
        ) : (
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke={p.b}
            strokeWidth="2"
            strokeDasharray="3 4"
            opacity="0.6"
          />
        )}

        <circle
          cx="50"
          cy="50"
          r="36"
          fill={isChallenger ? `url(#${gradId}-rainbow)` : `url(#${gradId})`}
          stroke="#fff"
          strokeWidth="3"
        />
        <circle cx="50" cy="50" r="28" fill="none" stroke={p.b} strokeWidth="1.5" opacity="0.7" />

        {/* Insignia per tier */}
        {isChallenger ? (
          // Crown for Challenger
          <g>
            <path
              d="M 30 60 L 35 38 L 45 52 L 50 32 L 55 52 L 65 38 L 70 60 Z"
              fill="#fff"
              stroke={p.ring}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <circle cx="35" cy="36" r="2.5" fill={p.ring} />
            <circle cx="50" cy="30" r="2.5" fill={p.ring} />
            <circle cx="65" cy="36" r="2.5" fill={p.ring} />
            <rect x="30" y="60" width="40" height="3" fill={p.b} opacity="0.8" />
          </g>
        ) : isMaster ? (
          // Diamond + chevron for Master
          <g>
            <polygon
              points="50,28 64,42 50,72 36,42"
              fill="#fff"
              opacity="0.95"
              stroke={p.ring}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <polygon points="50,28 64,42 50,46 36,42" fill={p.a} opacity="0.7" />
            <line x1="42" y1="42" x2="50" y2="56" stroke={p.ring} strokeWidth="1.2" />
            <line x1="58" y1="42" x2="50" y2="56" stroke={p.ring} strokeWidth="1.2" />
          </g>
        ) : (
          // Star for lower tiers
          <polygon
            points="50,28 56,46 75,46 60,57 66,75 50,64 34,75 40,57 25,46 44,46"
            fill="#fff"
            opacity="0.95"
          />
        )}

        <ellipse cx="42" cy="38" rx="14" ry="8" fill="rgba(255,255,255,0.35)" />
      </svg>
    </div>
  );
}
